/**
 * DS-160 Parser
 * Extracts structured data from DS-160 confirmation page PDFs
 */

import type { DS160Data, EmploymentEntry, EducationEntry, SocialMediaEntry } from './types';

/**
 * Parse DS-160 confirmation page text into structured data
 */
export function parseDS160(text: string): DS160Data {
  const normalizedText = text.replace(/\r\n/g, '\n');

  return {
    confirmationNumber: extractConfirmationNumber(normalizedText),
    applicationId: extractApplicationId(normalizedText),
    personal: extractPersonalInfo(normalizedText),
    contact: extractContactInfo(normalizedText),
    passport: extractPassportInfo(normalizedText),
    travel: extractTravelInfo(normalizedText),
    employment: extractEmploymentHistory(normalizedText),
    education: extractEducation(normalizedText),
    socialMedia: extractSocialMedia(normalizedText),
    usContacts: extractUSContacts(normalizedText),
    previousUSTravel: extractPreviousUSTravel(normalizedText),
    securityAnswers: {},
  };
}

/**
 * Extract confirmation number (format: AA + 10 digits)
 */
function extractConfirmationNumber(text: string): string | undefined {
  // DS-160 confirmation numbers are typically AA followed by 10 digits
  const patterns = [
    /Confirmation(?:\s+Number)?[:\s]*([A-Z]{2}\d{10})/i,
    /Barcode[:\s]*([A-Z]{2}\d{10})/i,
    /\b([A-Z]{2}\d{10})\b/,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return match[1];
    }
  }
  return undefined;
}

/**
 * Extract application ID
 */
function extractApplicationId(text: string): string | undefined {
  const match = text.match(/Application\s+ID[:\s]*(\S+)/i);
  return match ? match[1] : undefined;
}

/**
 * Extract personal information
 */
function extractPersonalInfo(text: string): DS160Data['personal'] {
  const personal: DS160Data['personal'] = {};

  // Surname
  const surnameMatch = text.match(/(?:Surname|Family Name|Last Name)[:\s]*([A-Za-z\-\s]+?)(?:\n|$|Given)/i);
  if (surnameMatch) personal.surname = cleanName(surnameMatch[1]);

  // Given name
  const givenMatch = text.match(/(?:Given Name|First Name)[:\s]*([A-Za-z\-\s]+?)(?:\n|$|Middle|Full)/i);
  if (givenMatch) personal.givenName = cleanName(givenMatch[1]);

  // Full name in native alphabet
  const nativeMatch = text.match(/(?:Full Name in Native Alphabet|Native Alphabet)[:\s]*(.+?)(?:\n|$)/i);
  if (nativeMatch) personal.fullNameNative = nativeMatch[1].trim();

  // Date of birth
  const dobPatterns = [
    /(?:Date of Birth|DOB|Birth Date)[:\s]*(\d{1,2}[\-\/]\w{3}[\-\/]\d{4})/i,
    /(?:Date of Birth|DOB|Birth Date)[:\s]*(\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4})/i,
    /(?:Date of Birth|DOB|Birth Date)[:\s]*(\w+\s+\d{1,2},?\s+\d{4})/i,
  ];
  for (const pattern of dobPatterns) {
    const match = text.match(pattern);
    if (match) {
      personal.dateOfBirth = match[1].trim();
      break;
    }
  }

  // City of birth
  const cityBirthMatch = text.match(/(?:City of Birth|Birth City)[:\s]*([A-Za-z\s\-]+?)(?:\n|$|Country|State)/i);
  if (cityBirthMatch) personal.cityOfBirth = cityBirthMatch[1].trim();

  // Country of birth
  const countryBirthMatch = text.match(/(?:Country(?:\/Region)? of Birth)[:\s]*([A-Za-z\s\-]+?)(?:\n|$)/i);
  if (countryBirthMatch) personal.countryOfBirth = countryBirthMatch[1].trim();

  // Nationality
  const nationalityMatch = text.match(/(?:Nationality|Citizenship)[:\s]*([A-Za-z\s\-]+?)(?:\n|$)/i);
  if (nationalityMatch) personal.nationality = nationalityMatch[1].trim();

  // Gender
  const genderMatch = text.match(/(?:Sex|Gender)[:\s]*(Male|Female|M|F)/i);
  if (genderMatch) {
    personal.gender = genderMatch[1].toLowerCase().startsWith('m') ? 'Male' : 'Female';
  }

  // Marital status
  const maritalMatch = text.match(/(?:Marital Status)[:\s]*(Single|Married|Divorced|Widowed|Separated|Civil Union|Domestic Partner)/i);
  if (maritalMatch) personal.maritalStatus = maritalMatch[1];

  // National ID
  const nationalIdMatch = text.match(/(?:National Identification Number|National ID)[:\s]*(\S+)/i);
  if (nationalIdMatch) personal.nationalIdNumber = nationalIdMatch[1];

  return personal;
}

/**
 * Extract contact information
 */
function extractContactInfo(text: string): DS160Data['contact'] {
  const contact: DS160Data['contact'] = {};

  // Email
  const emailMatch = text.match(/(?:Email|E-mail)[:\s]*([a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})/i);
  if (emailMatch) contact.email = emailMatch[1].toLowerCase();

  // Phone
  const phonePatterns = [
    /(?:Phone|Telephone|Tel)[:\s]*(\+?[\d\s\-\(\)]{10,})/i,
    /(?:Mobile|Cell)[:\s]*(\+?[\d\s\-\(\)]{10,})/i,
  ];
  for (const pattern of phonePatterns) {
    const match = text.match(pattern);
    if (match) {
      contact.phone = match[1].trim();
      break;
    }
  }

  // Address components - typically in "Address and Phone" section
  const addressSection = extractSection(text, /Address and Phone/i, /Passport|Travel|U\.S\. Contact/i);
  if (addressSection) {
    const streetMatch = addressSection.match(/(?:Street Address|Address Line 1)[:\s]*(.+?)(?:\n|$)/i);
    if (streetMatch) contact.address = streetMatch[1].trim();

    const cityMatch = addressSection.match(/(?:City)[:\s]*([A-Za-z\s\-]+?)(?:\n|$|State|Province)/i);
    if (cityMatch) contact.city = cityMatch[1].trim();

    const stateMatch = addressSection.match(/(?:State|Province)[:\s]*([A-Za-z\s\-]+?)(?:\n|$|Postal|Zip|Country)/i);
    if (stateMatch) contact.state = stateMatch[1].trim();

    const postalMatch = addressSection.match(/(?:Postal Code|ZIP Code|Zip)[:\s]*(\S+)/i);
    if (postalMatch) contact.postalCode = postalMatch[1].trim();

    const countryMatch = addressSection.match(/(?:Country(?:\/Region)?)[:\s]*([A-Za-z\s\-]+?)(?:\n|$)/i);
    if (countryMatch) contact.country = countryMatch[1].trim();
  }

  return contact;
}

/**
 * Extract passport information
 */
function extractPassportInfo(text: string): DS160Data['passport'] {
  const passport: DS160Data['passport'] = {};

  // Passport number
  const passportNumMatch = text.match(/(?:Passport Number|Passport No)[:\s]*([A-Z0-9]+)/i);
  if (passportNumMatch) passport.number = passportNumMatch[1].toUpperCase();

  // Issuance details
  const issuanceCityMatch = text.match(/(?:Passport Issuance City|City of Issuance)[:\s]*([A-Za-z\s\-]+?)(?:\n|$)/i);
  if (issuanceCityMatch) passport.issuanceCity = issuanceCityMatch[1].trim();

  const issuanceCountryMatch = text.match(/(?:Passport Issuance Country|Country of Issuance)[:\s]*([A-Za-z\s\-]+?)(?:\n|$)/i);
  if (issuanceCountryMatch) passport.issuanceCountry = issuanceCountryMatch[1].trim();

  // Dates
  const issuanceDateMatch = text.match(/(?:Issuance Date|Date of Issuance)[:\s]*(\d{1,2}[\-\/]\w{3}[\-\/]\d{4}|\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4})/i);
  if (issuanceDateMatch) passport.issuanceDate = issuanceDateMatch[1];

  const expirationMatch = text.match(/(?:Expiration Date|Expiry Date|Valid Until)[:\s]*(\d{1,2}[\-\/]\w{3}[\-\/]\d{4}|\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4})/i);
  if (expirationMatch) passport.expirationDate = expirationMatch[1];

  return passport;
}

/**
 * Extract travel information
 */
function extractTravelInfo(text: string): DS160Data['travel'] {
  const travel: DS160Data['travel'] = {};

  // Purpose of trip
  const purposeMatch = text.match(/(?:Purpose of Trip|Purpose of Travel|Visa Class)[:\s]*([A-Za-z\-\s]+?)(?:\n|$)/i);
  if (purposeMatch) travel.purpose = purposeMatch[1].trim();

  // Dates
  const arrivalMatch = text.match(/(?:Intended Date of Arrival|Arrival Date)[:\s]*(\d{1,2}[\-\/]\w{3}[\-\/]\d{4}|\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4})/i);
  if (arrivalMatch) travel.arrivalDate = arrivalMatch[1];

  const departureMatch = text.match(/(?:Intended Date of Departure|Departure Date|Length of Stay)[:\s]*(\d{1,2}[\-\/]\w{3}[\-\/]\d{4}|\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4})/i);
  if (departureMatch) travel.departureDate = departureMatch[1];

  // US address
  const usAddressMatch = text.match(/(?:U\.?S\.? Street Address|Address Where You Will Stay)[:\s]*(.+?)(?:\n|$)/i);
  if (usAddressMatch) travel.stayAddress = usAddressMatch[1].trim();

  // Who is paying
  const payingMatch = text.match(/(?:Who Is Paying|Person Paying)[:\s]*(.+?)(?:\n|$)/i);
  if (payingMatch) travel.payingPerson = payingMatch[1].trim();

  return travel;
}

/**
 * Extract employment history
 */
function extractEmploymentHistory(text: string): EmploymentEntry[] {
  const employment: EmploymentEntry[] = [];

  // Find the employment section
  const employmentSection = extractSection(
    text,
    /(?:Work\/Education|Present Work|Employment|Previous Employment)/i,
    /(?:Education|Security|Additional)/i
  );

  if (!employmentSection) return employment;

  // Try to find individual employer blocks
  // DS-160 typically shows employer name, title, dates, address
  const employerBlocks = employmentSection.split(/(?=(?:Employer Name|Company Name|Organization Name)[:\s])/i);

  for (const block of employerBlocks) {
    if (block.trim().length < 20) continue;

    const entry: EmploymentEntry = {};

    // Employer name
    const employerMatch = block.match(/(?:Employer Name|Company Name|Organization Name)[:\s]*(.+?)(?:\n|$)/i);
    if (employerMatch) entry.employer = employerMatch[1].trim();

    // Job title
    const titleMatch = block.match(/(?:Job Title|Title|Position)[:\s]*(.+?)(?:\n|$)/i);
    if (titleMatch) entry.title = titleMatch[1].trim();

    // Dates
    const startMatch = block.match(/(?:Start Date|From|Date From)[:\s]*(\d{1,2}[\-\/]\w{3}[\-\/]\d{4}|\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4}|\w+\s+\d{4})/i);
    if (startMatch) entry.startDate = startMatch[1];

    const endMatch = block.match(/(?:End Date|To|Date To)[:\s]*(\d{1,2}[\-\/]\w{3}[\-\/]\d{4}|\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4}|\w+\s+\d{4}|Present|Current)/i);
    if (endMatch) {
      entry.endDate = endMatch[1];
      entry.isCurrent = /present|current/i.test(endMatch[1]);
    }

    // Address
    const addressMatch = block.match(/(?:Employer Address|Address)[:\s]*(.+?)(?:\n|$)/i);
    if (addressMatch) entry.address = addressMatch[1].trim();

    // City
    const cityMatch = block.match(/(?:City)[:\s]*([A-Za-z\s\-]+?)(?:\n|$|State|Country)/i);
    if (cityMatch) entry.city = cityMatch[1].trim();

    // Country
    const countryMatch = block.match(/(?:Country)[:\s]*([A-Za-z\s\-]+?)(?:\n|$)/i);
    if (countryMatch) entry.country = countryMatch[1].trim();

    // Supervisor
    const supervisorMatch = block.match(/(?:Supervisor|Manager)[:\s]*(.+?)(?:\n|$)/i);
    if (supervisorMatch) entry.supervisor = supervisorMatch[1].trim();

    // Duties
    const dutiesMatch = block.match(/(?:Duties|Job Duties|Description)[:\s]*(.+?)(?:\n\n|$)/is);
    if (dutiesMatch) entry.duties = dutiesMatch[1].trim();

    if (entry.employer || entry.title) {
      employment.push(entry);
    }
  }

  return employment;
}

/**
 * Extract education history
 */
function extractEducation(text: string): EducationEntry[] {
  const education: EducationEntry[] = [];

  const educationSection = extractSection(
    text,
    /(?:Education|School|University|College)/i,
    /(?:Employment|Work|Security|Additional)/i
  );

  if (!educationSection) return education;

  const schoolBlocks = educationSection.split(/(?=(?:School Name|Institution Name|University|College)[:\s])/i);

  for (const block of schoolBlocks) {
    if (block.trim().length < 20) continue;

    const entry: EducationEntry = {};

    // Institution
    const institutionMatch = block.match(/(?:School Name|Institution Name|University|College)[:\s]*(.+?)(?:\n|$)/i);
    if (institutionMatch) entry.institution = institutionMatch[1].trim();

    // Degree
    const degreeMatch = block.match(/(?:Degree|Qualification)[:\s]*(.+?)(?:\n|$)/i);
    if (degreeMatch) entry.degree = degreeMatch[1].trim();

    // Field of study
    const fieldMatch = block.match(/(?:Course of Study|Field of Study|Major)[:\s]*(.+?)(?:\n|$)/i);
    if (fieldMatch) entry.fieldOfStudy = fieldMatch[1].trim();

    // Dates
    const startMatch = block.match(/(?:Start Date|From|Date From)[:\s]*(\d{1,2}[\-\/]\w{3}[\-\/]\d{4}|\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4}|\w+\s+\d{4})/i);
    if (startMatch) entry.startDate = startMatch[1];

    const endMatch = block.match(/(?:End Date|To|Date To)[:\s]*(\d{1,2}[\-\/]\w{3}[\-\/]\d{4}|\d{1,2}[\-\/]\d{1,2}[\-\/]\d{4}|\w+\s+\d{4}|Present|Current)/i);
    if (endMatch) entry.endDate = endMatch[1];

    // Address
    const cityMatch = block.match(/(?:City)[:\s]*([A-Za-z\s\-]+?)(?:\n|$|State|Country)/i);
    if (cityMatch) entry.city = cityMatch[1].trim();

    const countryMatch = block.match(/(?:Country)[:\s]*([A-Za-z\s\-]+?)(?:\n|$)/i);
    if (countryMatch) entry.country = countryMatch[1].trim();

    if (entry.institution) {
      education.push(entry);
    }
  }

  return education;
}

/**
 * Extract social media handles
 */
function extractSocialMedia(text: string): SocialMediaEntry[] {
  const socialMedia: SocialMediaEntry[] = [];

  // Common social media patterns in DS-160
  const platforms = [
    { name: 'Facebook', patterns: [/Facebook[:\s]*(@?\S+)/i, /facebook\.com\/(\S+)/i] },
    { name: 'Twitter', patterns: [/Twitter[:\s]*(@?\S+)/i, /twitter\.com\/(\S+)/i, /X \(Twitter\)[:\s]*(@?\S+)/i] },
    { name: 'Instagram', patterns: [/Instagram[:\s]*(@?\S+)/i, /instagram\.com\/(\S+)/i] },
    { name: 'LinkedIn', patterns: [/LinkedIn[:\s]*(\S+)/i, /linkedin\.com\/in\/(\S+)/i] },
    { name: 'YouTube', patterns: [/YouTube[:\s]*(\S+)/i, /youtube\.com\/(?:user\/|channel\/)?(\S+)/i] },
  ];

  for (const platform of platforms) {
    for (const pattern of platform.patterns) {
      const match = text.match(pattern);
      if (match && match[1] && match[1].toLowerCase() !== 'none' && match[1].toLowerCase() !== 'n/a') {
        socialMedia.push({
          platform: platform.name,
          handle: match[1].replace(/^@/, ''),
        });
        break;
      }
    }
  }

  return socialMedia;
}

/**
 * Extract US contacts
 */
function extractUSContacts(text: string): DS160Data['usContacts'] {
  const contacts: DS160Data['usContacts'] = [];

  const contactSection = extractSection(
    text,
    /U\.?S\.? Contact/i,
    /(?:Family|Travel|Security)/i
  );

  if (!contactSection) return contacts;

  const contact: DS160Data['usContacts'][0] = {};

  const nameMatch = contactSection.match(/(?:Contact Name|Name)[:\s]*(.+?)(?:\n|$)/i);
  if (nameMatch) contact.name = nameMatch[1].trim();

  const orgMatch = contactSection.match(/(?:Organization|Company)[:\s]*(.+?)(?:\n|$)/i);
  if (orgMatch) contact.organization = orgMatch[1].trim();

  const relationMatch = contactSection.match(/(?:Relationship)[:\s]*(.+?)(?:\n|$)/i);
  if (relationMatch) contact.relationship = relationMatch[1].trim();

  const phoneMatch = contactSection.match(/(?:Phone|Telephone)[:\s]*(\+?[\d\s\-\(\)]{10,})/i);
  if (phoneMatch) contact.phone = phoneMatch[1].trim();

  const emailMatch = contactSection.match(/(?:Email)[:\s]*([a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})/i);
  if (emailMatch) contact.email = emailMatch[1].toLowerCase();

  if (contact.name || contact.organization) {
    contacts.push(contact);
  }

  return contacts;
}

/**
 * Extract previous US travel
 */
function extractPreviousUSTravel(text: string): DS160Data['previousUSTravel'] {
  const visits: DS160Data['previousUSTravel'] = [];

  const travelSection = extractSection(
    text,
    /Previous U\.?S\.? Travel/i,
    /(?:Security|Additional|SEVIS)/i
  );

  if (!travelSection) return visits;

  // Check if they've been to US before
  if (/Have you ever been to the U\.?S\.?\??\s*No/i.test(travelSection)) {
    return visits;
  }

  // Try to extract visit details
  const visitBlocks = travelSection.split(/(?=Date of Arrival|Arrival Date)/i);

  for (const block of visitBlocks) {
    if (block.trim().length < 10) continue;

    const visit: DS160Data['previousUSTravel'][0] = {};

    const arrivalMatch = block.match(/(?:Date of Arrival|Arrival Date)[:\s]*(\S+)/i);
    if (arrivalMatch) visit.arrivalDate = arrivalMatch[1];

    const departureMatch = block.match(/(?:Date of Departure|Departure Date)[:\s]*(\S+)/i);
    if (departureMatch) visit.departureDate = departureMatch[1];

    const visaMatch = block.match(/(?:Visa Type|Visa Class)[:\s]*([A-Z]\-?\d*)/i);
    if (visaMatch) visit.visaType = visaMatch[1];

    const lengthMatch = block.match(/(?:Length of Stay)[:\s]*(.+?)(?:\n|$)/i);
    if (lengthMatch) visit.lengthOfStay = lengthMatch[1].trim();

    if (visit.arrivalDate || visit.visaType) {
      visits.push(visit);
    }
  }

  return visits;
}

// Helper functions

function extractSection(text: string, startPattern: RegExp, endPattern: RegExp): string | null {
  const startMatch = text.match(startPattern);
  if (!startMatch) return null;

  const startIndex = startMatch.index! + startMatch[0].length;
  const remainingText = text.substring(startIndex);

  const endMatch = remainingText.match(endPattern);
  const endIndex = endMatch ? endMatch.index! : remainingText.length;

  return remainingText.substring(0, endIndex);
}

function cleanName(name: string): string {
  return name
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^A-Za-z\s\-']/g, '');
}
